/**
 * ATTACH-01 surfaces for phone calls (Seth 2026-08-28).
 *
 * <AttachFromCallCard custno> — sits at the top of a 360: when someone is (or
 * just was, ≤45 min) on the phone with an UNMATCHED number and the dispatcher
 * lands on this customer, offer one tap to attach that number — and the email
 * the AI heard, when there is one. Attached rows land in the contact store,
 * which the phone matcher reads (MATCH-02), so the next call from that number
 * screen-pops this customer automatically.
 *
 * <FromCallStrip onStart> — sits on customer search: when a recent unmatched
 * call has AI notes, show what the AI heard as chips and offer "Start a new
 * customer with these details" (everything prefilled, nothing auto-saved).
 */
import { useState } from "react";
import { Phone, Sparkles, X, Check, Loader2 } from "lucide-react";
import { useAccountEndpoints, useAttachEndpoint, last10, prettyPhone } from "@/features/customer-360/attachEndpoint";
import { useRecentCalls, type CallEvent } from "./calls";

const lowmail = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** Newest recent inbound call with a real number that matched NOBODY. */
function pickUnmatched(calls: CallEvent[]): CallEvent | null {
  return (
    calls.find((c) => !c.custno && last10(c.from_e164).length === 10 && (c.state === "ringing" || c.state === "answered")) ??
    calls.find((c) => !c.custno && last10(c.from_e164).length === 10) ??
    null
  );
}

export function AttachFromCallCard({ custno }: { custno: string }) {
  const calls = useRecentCalls().data ?? [];
  const eps = useAccountEndpoints(custno).data;
  const attach = useAttachEndpoint();
  const [hidden, setHidden] = useState<string | null>(null); // call id dismissed
  const [done, setDone] = useState<string[]>([]);

  const call = pickUnmatched(calls);
  if (!call || !eps || hidden === call.id) return null;
  const p10 = last10(call.from_e164);
  const phoneKnown = eps.phones.has(p10) || done.includes(`p:${p10}`);
  const aiEmail = (call.ai_extract?.emails ?? []).map(lowmail).find((e) => e && !eps.emails.has(e));
  const emailDone = aiEmail ? done.includes(`e:${aiEmail}`) : false;
  if (phoneKnown && (!aiEmail || emailDone)) return null;

  const callerName = call.ai_extract?.caller_name ?? null;
  const live = call.state === "ringing" || call.state === "answered";

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-xl border border-sky-200 bg-sky-50/70 px-3.5 py-2.5">
      <Phone className="h-4 w-4 shrink-0 text-sky-600" />
      <span className="min-w-0 text-[13px] text-slate-700">
        {live ? "On the phone with" : "Just spoke with"}{" "}
        <span className="font-bold">{callerName ?? prettyPhone(call.from_e164)}</span>
        {callerName ? ` (${prettyPhone(call.from_e164)})` : ""} — not on this account yet.
        {call.ai_summary && (
          <span className="ml-1 inline-flex items-center gap-1 text-[12px] text-slate-500">
            <Sparkles className="h-3 w-3 text-violet-500" /> {call.ai_summary}
          </span>
        )}
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        {!phoneKnown && (
          <button
            type="button"
            disabled={attach.isPending}
            onClick={() =>
              attach.mutate(
                { custno, name: callerName ?? "Phone contact", phone: call.from_e164, source: "call", note: "Attached from a phone call — number wasn't on the account" },
                { onSuccess: () => setDone((d) => [...d, `p:${p10}`]) },
              )
            }
            className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
          >
            {attach.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Attach {prettyPhone(call.from_e164)}
          </button>
        )}
        {aiEmail && !emailDone && (
          <button
            type="button"
            disabled={attach.isPending}
            onClick={() =>
              attach.mutate(
                { custno, name: callerName ?? "Email contact", email: aiEmail, source: "call", note: "Heard on a phone call — email wasn't on the account" },
                { onSuccess: () => setDone((d) => [...d, `e:${aiEmail}`]) },
              )
            }
            className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-2.5 py-1 text-xs font-semibold text-sky-700 transition-colors hover:bg-sky-100 disabled:opacity-50"
          >
            <Check className="h-3 w-3" /> Attach {aiEmail}
          </button>
        )}
        <button
          type="button"
          onClick={() => setHidden(call.id)}
          title="Not this customer — dismiss"
          className="rounded p-1 text-slate-400 hover:bg-sky-100 hover:text-slate-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}

export interface CallSeed {
  firstname?: string;
  lastname?: string;
  phone?: string;
  email?: string;
  add1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** What the AI heard on a recent unmatched call, as a create-ready strip. */
export function FromCallStrip({ onStart }: { onStart: (seed: CallSeed) => void }) {
  const calls = useRecentCalls().data ?? [];
  const [hidden, setHidden] = useState<string | null>(null);
  const call = calls.find((c) => !c.custno && !!c.ai_extract && (c.ai_extract.caller_name || (c.ai_extract.phones ?? []).length || c.ai_extract.address?.add1));
  if (!call || hidden === call.id) return null;
  const x = call.ai_extract!;
  const name = (x.caller_name ?? "").trim();
  const [firstname, ...rest] = name.split(/\s+/);
  const seed: CallSeed = {
    firstname: firstname || undefined,
    lastname: rest.join(" ") || undefined,
    phone: prettyPhone(x.phones?.[0] ?? call.from_e164),
    email: x.emails?.[0] ?? undefined,
    add1: x.address?.add1 ?? undefined,
    city: x.address?.city ?? undefined,
    state: x.address?.state ?? undefined,
    zip: x.address?.zip ?? undefined,
  };
  const chips = [name || null, x.address?.add1 ?? null, prettyPhone(x.phones?.[0] ?? call.from_e164) || null, x.emails?.[0] ?? null].filter(Boolean) as string[];

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-violet-200 bg-violet-50/60 px-3.5 py-2.5">
      <Sparkles className="h-4 w-4 shrink-0 text-violet-600" />
      <span className="text-[13px] font-semibold text-slate-700">From the call:</span>
      {chips.map((c) => (
        <span key={c} className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-violet-200">{c}</span>
      ))}
      {call.ai_summary && <span className="text-xs text-slate-500">· {call.ai_summary}</span>}
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={() => onStart(seed)}
          className="rounded-md bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-violet-700"
        >
          Start a new customer with these details
        </button>
        <button type="button" onClick={() => setHidden(call.id)} title="Dismiss" className="rounded p-1 text-slate-400 hover:bg-violet-100 hover:text-slate-600">
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}
